import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { useNavigate } from "react-router-dom";

import {
  ArrowRight,
  BookOpenText,
  Check,
  Flower2,
  Moon,
  Quote,
  Sun,
  Sunrise,
} from "lucide-react";

import {
  api,
  StorageKeys,
} from "../lib/api";

import {
  storage,
  todayKey,
} from "../lib/storage";

import {
  useI18n,
} from "../lib/i18n";


/* =========================================================
   CONFIG
========================================================= */

const MORNING_THEME_KEY =
  "gs_morning_ritual_theme";

const CLOSE_MS = 140;


/* =========================================================
   MULTILINGUAL FRONTEND COPY
========================================================= */

const MORNING_UI_COPY = {
  en: {
    ritual: "GurSetu",
    presence: "Begin in remembrance",
    blessing: "A quiet beginning with Gurbani",
    hukamSub: "A word to begin with",
    reflection: "Today's Reflection",
    finalMessage:
      "Begin gently. Carry the Guru's remembrance into everything you do today.",
    light: "Light",
    dark: "Dark",
  },

  hi: {
    ritual: "पवित्र सुबह",
    presence: "सिमरन के साथ शुरुआत",
    blessing: "गुरबाणी के साथ शांत शुरुआत",
    hukamSub: "दिन की शुरुआत एक संदेश के साथ",
    reflection: "आज का चिंतन",
    finalMessage:
      "दिन की शुरुआत शांति से करें और गुरु की याद को अपने हर काम में साथ रखें।",
    light: "लाइट",
    dark: "डार्क",
  },

  pa: {
    ritual: "ਪਵਿੱਤਰ ਸਵੇਰ",
    presence: "ਸਿਮਰਨ ਨਾਲ ਸ਼ੁਰੂਆਤ",
    blessing: "ਗੁਰਬਾਣੀ ਨਾਲ ਸ਼ਾਂਤ ਸ਼ੁਰੂਆਤ",
    hukamSub: "ਦਿਨ ਦੀ ਸ਼ੁਰੂਆਤ ਇੱਕ ਬਚਨ ਨਾਲ",
    reflection: "ਅੱਜ ਦਾ ਵਿਚਾਰ",
    finalMessage:
      "ਦਿਨ ਹੌਲੇ ਤੇ ਸ਼ਾਂਤ ਮਨ ਨਾਲ ਸ਼ੁਰੂ ਕਰੋ ਅਤੇ ਗੁਰੂ ਦੀ ਯਾਦ ਨੂੰ ਹਰ ਕੰਮ ਵਿੱਚ ਨਾਲ ਰੱਖੋ।",
    light: "ਲਾਈਟ",
    dark: "ਡਾਰਕ",
  },
};


/* =========================================================
   THEME
========================================================= */

function getInitialMorningTheme() {
  if (
    typeof window === "undefined" ||
    typeof document === "undefined"
  ) {
    return "dark";
  }

  try {
    const saved =
      localStorage.getItem(
        MORNING_THEME_KEY
      );

    if (
      saved === "light" ||
      saved === "dark"
    ) {
      return saved;
    }

    /*
     * Follow GurSetu's app-wide dark theme
     * on first Morning Splash visit.
     */
    if (
      document.documentElement
        .classList.contains("dark")
    ) {
      return "dark";
    }
  } catch {
    // Safe fallback below.
  }

  /* GurSetu default = dark */
  return "dark";
}


/* =========================================================
   SMALL HELPERS
========================================================= */

function safeText(value) {
  if (
    value === null ||
    value === undefined
  ) {
    return "";
  }

  return String(value);
}


function CardShell({
  children,
  colors,
  strong = false,
  testId,
}) {
  return (
    <section
      data-testid={testId}
      className="
        relative
        overflow-hidden
        rounded-[24px]
        border
        p-5
      "
      style={{
        background:
          strong
            ? colors.cardStrong
            : colors.card,

        borderColor:
          colors.border,

        boxShadow:
          colors.isDark
            ? "0 14px 34px rgba(0,0,0,0.28)"
            : "0 14px 34px rgba(74,48,10,0.08)",

        WebkitBackfaceVisibility:
          "hidden",

        backfaceVisibility:
          "hidden",
      }}
    >
      {/* Static gold top hairline — no moving shine */}
      <div
        className="
          pointer-events-none
          absolute
          inset-x-8
          top-0
          h-px
        "
        style={{
          background:
            `linear-gradient(to right, transparent, ${colors.goldSoft}, transparent)`,

          opacity: 0.64,
        }}
      />

      {children}
    </section>
  );
}


function CardHeading({
  icon: Icon,
  label,
  colors,
  subtitle,
}) {
  return (
    <div
      className="
        flex
        items-center
        gap-2.5
      "
    >
      <div
        className="
          grid
          h-9
          w-9
          shrink-0
          place-items-center
          rounded-xl
          border
        "
        style={{
          background:
            colors.iconBg,

          borderColor:
            colors.border,

          color:
            colors.gold,
        }}
      >
        <Icon size={16} />
      </div>

      <div className="min-w-0">
        <p
          className="
            text-[9px]
            font-black
            uppercase
            tracking-[1.8px]
          "
          style={{
            color:
              colors.gold,
          }}
        >
          {label}
        </p>

        {subtitle ? (
          <p
            className="
              mt-0.5
              text-[9px]
              font-semibold
              tracking-[0.5px]
            "
            style={{
              color:
                colors.muted,
            }}
          >
            {subtitle}
          </p>
        ) : null}
      </div>
    </div>
  );
}


/* =========================================================
   MAIN
========================================================= */

export default function MorningSplash() {
  const [show, setShow] =
    useState(false);

  const [closing, setClosing] =
    useState(false);

  const [quote, setQuote] =
    useState(null);

  const [challenge, setChallenge] =
    useState(null);

  const [hukam, setHukam] =
    useState(null);

  const [name, setName] =
    useState("Ji");

  const [morningTheme, setMorningTheme] =
    useState(
      getInitialMorningTheme
    );

  const closeTimerRef =
    useRef(null);

  const navigate =
    useNavigate();

  const {
    t,
    lang,
  } = useI18n();

  const isDark =
    morningTheme === "dark";

  const morningCopy =
    MORNING_UI_COPY[lang] ||
    MORNING_UI_COPY.en;


  /* =======================================================
     INITIAL LOAD
  ======================================================= */

  useEffect(() => {
    if (
      storage.get(
        StorageKeys.MorningRitualDate,
        ""
      ) === todayKey()
    ) {
      return;
    }

    if (
      sessionStorage.getItem(
        "gs_morning_shown"
      )
    ) {
      return;
    }

    setShow(true);

    const profile =
      storage.get(
        StorageKeys.UserProfile,
        {}
      ) || {};

    if (profile.fullName) {
      setName(
        String(profile.fullName)
          .trim()
          .split(/\s+/)[0] ||
          "Ji"
      );
    }

    const gid =
      storage.get(
        StorageKeys.PreferredGurudwara,
        ""
      ) ||
      "harmandir-sahib";

    let active = true;

    api
      .get("/daily/quote")
      .then((response) => {
        if (active) {
          setQuote(response);
        }
      })
      .catch(() => {});

    api
      .get("/daily/challenge")
      .then((response) => {
        if (active) {
          setChallenge(response);
        }
      })
      .catch(() => {});

    api
      .get(
        `/hukamnama/today/${gid}`
      )
      .then((response) => {
        if (active) {
          setHukam(response);
        }
      })
      .catch(() => {});

    return () => {
      active = false;
    };
  }, []);


  /* =======================================================
     MOBILE / WEBVIEW SCROLL LOCK
  ======================================================= */

  useEffect(() => {
    if (!show) {
      return;
    }

    const body =
      document.body;

    const root =
      document.documentElement;

    const oldBodyOverflow =
      body.style.overflow;

    const oldBodyOverscroll =
      body.style.overscrollBehavior;

    const oldRootOverflow =
      root.style.overflow;

    const oldRootOverscroll =
      root.style.overscrollBehavior;

    body.style.overflow =
      "hidden";

    body.style.overscrollBehavior =
      "none";

    root.style.overflow =
      "hidden";

    root.style.overscrollBehavior =
      "none";

    return () => {
      body.style.overflow =
        oldBodyOverflow;

      body.style.overscrollBehavior =
        oldBodyOverscroll;

      root.style.overflow =
        oldRootOverflow;

      root.style.overscrollBehavior =
        oldRootOverscroll;
    };
  }, [show]);


  /* =======================================================
     SAVE MORNING THEME
  ======================================================= */

  useEffect(() => {
    try {
      localStorage.setItem(
        MORNING_THEME_KEY,
        morningTheme
      );
    } catch {
      // ignore
    }
  }, [morningTheme]);


  /* =======================================================
     CLEAN TIMER
  ======================================================= */

  useEffect(() => {
    return () => {
      if (
        closeTimerRef.current
      ) {
        window.clearTimeout(
          closeTimerRef.current
        );
      }
    };
  }, []);


  /* =======================================================
     FINISH
  ======================================================= */

  const begin = (
    afterClose = null
  ) => {
    if (closing) {
      return;
    }

    storage.set(
      StorageKeys.MorningRitualDate,
      todayKey()
    );

    sessionStorage.setItem(
      "gs_morning_shown",
      "1"
    );

    /*
     * Very short opacity-only close.
     * No scale / blur / transform = no WebView tearing.
     */
    setClosing(true);

    closeTimerRef.current =
      window.setTimeout(() => {
        setShow(false);
        setClosing(false);

        if (
          typeof afterClose ===
          "function"
        ) {
          afterClose();
        }
      }, CLOSE_MS);
  };


  /* =======================================================
     GREETING
  ======================================================= */

  const hour =
    new Date().getHours();

  const greet =
    hour < 12
      ? t("good Morning")
      : hour < 17
      ? t("good Afternoon")
      : t("good Evening");


  /* =======================================================
     TRANSLATED CONTENT
  ======================================================= */

  const hukTr =
    hukam?.gurbani
      ? lang === "hi"
        ? hukam.gurbani
            .hindiTranslation
        : lang === "pa"
        ? hukam.gurbani
            .punjabiTranslation
        : hukam.gurbani
            .englishTranslation
      : "";

  const quoteText =
    quote
      ? lang === "hi"
        ? quote.hindi ||
          quote.english
        : lang === "pa"
        ? quote.punjabi ||
          quote.english
        : quote.english
      : "";


  /* =======================================================
     COLORS — OPAQUE / MOBILE-SAFE
  ======================================================= */

  const colors =
    useMemo(() => {
      if (isDark) {
        return {
          isDark: true,
          bg: "#070A0F",
          secondaryBg: "#0B1017",
          card: "#0F151E",
          cardStrong: "#111923",
          text: "#F5F1E8",
          textSoft: "#B8B1A6",
          muted: "#827C73",
          border:
            "rgba(245,190,72,0.17)",
          gold: "#F2B84B",
          goldSoft: "#F7D483",
          buttonText: "#171005",
          skip: "#918D84",
          iconBg:
            "rgba(242,184,75,0.09)",
        };
      }

      return {
        isDark: false,
        bg: "#F7F2E7",
        secondaryBg: "#FFFDF8",
        card: "#FFFDF8",
        cardStrong: "#FFFEFB",
        text: "#172033",
        textSoft: "#5F6470",
        muted: "#8E8D87",
        border:
          "rgba(187,131,35,0.18)",
        gold: "#B97708",
        goldSoft: "#D5A73F",
        buttonText: "#171005",
        skip: "#747780",
        iconBg:
          "rgba(185,119,8,0.08)",
      };
    }, [isDark]);


  if (!show) {
    return null;
  }


  /* =======================================================
     RENDER
  ======================================================= */

  return (
    <div
      data-testid="morning-splash"
      className="
        fixed
        inset-0
        z-[2147483600]
        overflow-y-auto
        overflow-x-hidden
        overscroll-contain
        no-scrollbar
      "
      style={{
        backgroundColor:
          colors.bg,

        backgroundImage:
          isDark
            ? "radial-gradient(circle at 50% 0%, rgba(242,184,75,0.16) 0%, rgba(242,184,75,0.055) 23%, transparent 44%), linear-gradient(180deg,#070A0F 0%,#0B1017 44%,#080C12 100%)"
            : "radial-gradient(circle at 50% 0%, rgba(246,194,85,0.28) 0%, rgba(255,222,145,0.11) 24%, transparent 45%), linear-gradient(180deg,#FFF9ED 0%,#F8F4EA 48%,#F4EFE4 100%)",

        opacity:
          closing ? 0 : 1,

        transition:
          `opacity ${CLOSE_MS}ms ease-out`,

        isolation:
          "isolate",

        contain:
          "paint",

        WebkitOverflowScrolling:
          "touch",

        overscrollBehavior:
          "contain",

        touchAction:
          "pan-y",

        WebkitBackfaceVisibility:
          "hidden",

        backfaceVisibility:
          "hidden",
      }}
    >
      {/* =====================================================
          STATIC BACKGROUND DECORATION
          No fixed child layers / no blur / no infinite motion.
      ===================================================== */}

      <div
        className="
          pointer-events-none
          absolute
          inset-x-0
          top-0
          h-[260px]
          overflow-hidden
        "
        aria-hidden="true"
      >
        <div
          className="
            absolute
            left-1/2
            top-[-115px]
            h-[235px]
            w-[235px]
            -translate-x-1/2
            rounded-full
          "
          style={{
            background:
              isDark
                ? "radial-gradient(circle, rgba(255,219,122,.42) 0%, rgba(229,151,33,.12) 46%, transparent 72%)"
                : "radial-gradient(circle, rgba(255,235,170,.92) 0%, rgba(246,184,61,.34) 40%, rgba(246,184,61,.06) 64%, transparent 74%)",
          }}
        />

        <div
          className="
            absolute
            left-1/2
            top-[238px]
            h-px
            w-[82%]
            max-w-[470px]
            -translate-x-1/2
          "
          style={{
            background:
              `linear-gradient(to right, transparent, ${colors.goldSoft}, transparent)`,

            opacity: 0.26,
          }}
        />
      </div>


      {/* =====================================================
          MAIN WRAPPER
      ===================================================== */}

      <div
        className="
          relative
          z-10
          mx-auto
          flex
          min-h-[100dvh]
          w-full
          max-w-[540px]
          flex-col
          px-4
          pb-[max(30px,env(safe-area-inset-bottom))]
          pt-[max(18px,env(safe-area-inset-top))]
          sm:px-6
        "
      >
        {/* =================================================
            TOP CONTROLS
        ================================================= */}

        <div
          className="
            flex
            min-h-[44px]
            items-center
            justify-end
          "
        >
          <div
            className="
              flex
              items-center
              gap-1
              rounded-full
              border
              p-1
            "
            style={{
              borderColor:
                colors.border,

              background:
                colors.secondaryBg,

              boxShadow:
                isDark
                  ? "0 8px 22px rgba(0,0,0,0.20)"
                  : "0 8px 20px rgba(90,58,10,0.08)",
            }}
          >
            <button
              type="button"
              onClick={() =>
                setMorningTheme(
                  "light"
                )
              }
              aria-label="Use light mode"
              className="
                flex
                h-9
                items-center
                gap-1.5
                rounded-full
                px-3
                text-[10px]
                font-extrabold
              "
              style={{
                background:
                  !isDark
                    ? "#F5B52E"
                    : "transparent",

                color:
                  !isDark
                    ? "#171005"
                    : colors.textSoft,
              }}
            >
              <Sun size={13} />

              <span
                className="
                  hidden
                  min-[390px]:inline
                "
              >
                {morningCopy.light}
              </span>
            </button>


            <button
              type="button"
              onClick={() =>
                setMorningTheme(
                  "dark"
                )
              }
              aria-label="Use dark mode"
              className="
                flex
                h-9
                items-center
                gap-1.5
                rounded-full
                px-3
                text-[10px]
                font-extrabold
              "
              style={{
                background:
                  isDark
                    ? "#E6AA32"
                    : "transparent",

                color:
                  isDark
                    ? "#171005"
                    : colors.textSoft,
              }}
            >
              <Moon size={13} />

              <span
                className="
                  hidden
                  min-[390px]:inline
                "
              >
                {morningCopy.dark}
              </span>
            </button>
          </div>
        </div>


        {/* =================================================
            HEADER — STATIC / COMPOSITOR SAFE
        ================================================= */}

        <header
          className="
            pt-4
            text-center
          "
        >
          <div
            className="
              mx-auto
              mb-4
              inline-flex
              items-center
              gap-2
              rounded-full
              border
              px-3
              py-1.5
            "
            style={{
              borderColor:
                colors.border,

              background:
                colors.secondaryBg,

              color:
                colors.gold,
            }}
          >
            <span
              className="
                h-1.5
                w-1.5
                rounded-full
                bg-amber-400
              "
            />

            <span
              className="
                text-[8px]
                font-black
                uppercase
                tracking-[2px]
              "
            >
              {morningCopy.ritual}
            </span>
          </div>


          <div
            className="
              relative
              mx-auto
              grid
              h-14
              w-14
              place-items-center
              overflow-hidden
              rounded-[18px]
              border
            "
            style={{
              background:
                colors.secondaryBg,

              borderColor:
                colors.border,

              color:
                colors.gold,

              boxShadow:
                isDark
                  ? "0 12px 30px rgba(0,0,0,.22)"
                  : "0 12px 26px rgba(137,87,9,.08)",
            }}
          >
            <Sunrise size={25} />

            <span
              className="
                absolute
                -bottom-0.5
                text-[8px]
                font-black
                text-amber-500/70
              "
            >
              ੴ
            </span>
          </div>


          <p
            className="
              mt-4
              text-[9px]
              font-black
              uppercase
              tracking-[3px]
            "
            style={{
              color:
                colors.gold,
            }}
          >
            {greet}
          </p>


          <h1
            className="
              mt-1
              break-words
              font-heading
              text-[40px]
              font-black
              leading-[1.04]
              tracking-[-0.8px]
              sm:text-[50px]
            "
            style={{
              color:
                colors.text,
            }}
          >
            {name}
          </h1>


          <p
            className="
              mx-auto
              mt-2
              max-w-[310px]
              text-[11px]
              font-bold
              leading-5
            "
            style={{
              color:
                colors.textSoft,
            }}
          >
            {morningCopy.blessing}
          </p>


          <p
            className="
              mx-auto
              mt-3
              max-w-sm
              text-[12px]
              font-semibold
              italic
              leading-5
            "
            style={{
              color:
                colors.goldSoft,
            }}
          >
            {t("fateh")}

            <span
              className="mx-1 opacity-45"
            >
              ·
            </span>

            {t("fatehReply")}
          </p>


          <div
            className="
              mx-auto
              mt-5
              flex
              items-center
              justify-center
              gap-2
            "
          >
            <span
              className="h-px w-10"
              style={{
                background:
                  `linear-gradient(to right, transparent, ${colors.goldSoft})`,
              }}
            />

            <span
              style={{
                color:
                  colors.gold,
              }}
            >
              ੴ
            </span>

            <span
              className="h-px w-10"
              style={{
                background:
                  `linear-gradient(to left, transparent, ${colors.goldSoft})`,
              }}
            />
          </div>
        </header>


        {/* =================================================
            CONTENT CARDS
        ================================================= */}

        <div
          className="
            mt-7
            space-y-3.5
          "
        >
          {/* HUKAMNAMA */}

          {hukam?.gurbani ? (
            <CardShell
              colors={colors}
              strong
              testId="morning-hukam-card"
            >
              <CardHeading
                icon={BookOpenText}
                label={t(
                  "todaysHukamnama"
                )}
                subtitle={
                  morningCopy.hukamSub
                }
                colors={colors}
              />

              <p
                className="
                  mt-4
                  line-clamp-4
                  font-gurmukhi
                  text-[18px]
                  font-semibold
                  leading-8
                "
                style={{
                  color:
                    colors.text,
                }}
              >
                {safeText(
                  hukam.gurbani
                    .unicode
                )}
              </p>

              {hukTr ? (
                <p
                  className="
                    mt-3
                    line-clamp-3
                    text-[12px]
                    font-medium
                    italic
                    leading-5
                  "
                  style={{
                    color:
                      colors.textSoft,
                  }}
                >
                  {hukTr}
                </p>
              ) : null}

              <div
                className="
                  mt-4
                  flex
                  flex-wrap
                  items-center
                  gap-x-2
                  gap-y-1
                  border-t
                  pt-3
                  text-[9px]
                  font-semibold
                "
                style={{
                  borderColor:
                    colors.border,

                  color:
                    colors.muted,
                }}
              >
                {hukam.angNumber ? (
                  <span>
                    Ang {hukam.angNumber}
                  </span>
                ) : null}

                {hukam.angNumber &&
                hukam.gurbani?.raag
                  ?.english ? (
                  <span>•</span>
                ) : null}

                {hukam.gurbani?.raag
                  ?.english ? (
                  <span>
                    {hukam.gurbani
                      .raag.english}
                  </span>
                ) : null}
              </div>
            </CardShell>
          ) : null}


          {/* QUOTE */}

          {quote ? (
            <CardShell
              colors={colors}
              testId="morning-quote-card"
            >
              <CardHeading
                icon={Quote}
                label={t(
                  "todaysQuote"
                )}
                colors={colors}
              />

              {quote.gurmukhi ? (
                <p
                  className="
                    mt-4
                    font-gurmukhi
                    text-[17px]
                    font-semibold
                    leading-7
                  "
                  style={{
                    color:
                      colors.goldSoft,
                  }}
                >
                  {quote.gurmukhi}
                </p>
              ) : null}

              {quoteText ? (
                <p
                  className="
                    mt-2
                    text-[15px]
                    font-semibold
                    italic
                    leading-7
                  "
                  style={{
                    color:
                      colors.text,
                  }}
                >
                  “{quoteText}”
                </p>
              ) : null}

              {quote.reflection ? (
                <div
                  className="
                    mt-4
                    border-t
                    pt-3
                  "
                  style={{
                    borderColor:
                      colors.border,
                  }}
                >
                  <p
                    className="
                      text-[9px]
                      font-black
                      uppercase
                      tracking-[1.5px]
                    "
                    style={{
                      color:
                        colors.gold,
                    }}
                  >
                    {morningCopy.reflection}
                  </p>

                  <p
                    className="
                      mt-1
                      text-[11px]
                      font-medium
                      leading-5
                    "
                    style={{
                      color:
                        colors.textSoft,
                    }}
                  >
                    {quote.reflection}
                  </p>
                </div>
              ) : null}
            </CardShell>
          ) : null}


          {/* CHALLENGE */}

          {challenge ? (
            <CardShell
              colors={colors}
              testId="morning-challenge-card"
            >
              <div
                className="
                  flex
                  items-start
                  gap-3
                "
              >
                <div
                  className="
                    grid
                    h-10
                    w-10
                    shrink-0
                    place-items-center
                    rounded-2xl
                    border
                  "
                  style={{
                    background:
                      colors.iconBg,

                    borderColor:
                      colors.border,

                    color:
                      colors.gold,
                  }}
                >
                  <Flower2 size={18} />
                </div>

                <div
                  className="
                    min-w-0
                    flex-1
                  "
                >
                  <p
                    className="
                      text-[9px]
                      font-black
                      uppercase
                      tracking-[1.8px]
                    "
                    style={{
                      color:
                        colors.gold,
                    }}
                  >
                    {t(
                      "todaysChallenge"
                    )}
                  </p>

                  <h3
                    className="
                      mt-2
                      text-[17px]
                      font-extrabold
                      leading-6
                    "
                    style={{
                      color:
                        colors.text,
                    }}
                  >
                    {safeText(
                      challenge.title
                    )}
                  </h3>

                  {challenge.description ? (
                    <p
                      className="
                        mt-1.5
                        text-[11px]
                        font-medium
                        leading-5
                      "
                      style={{
                        color:
                          colors.textSoft,
                      }}
                    >
                      {challenge.description}
                    </p>
                  ) : null}
                </div>

                <div
                  className="
                    grid
                    h-7
                    w-7
                    shrink-0
                    place-items-center
                    rounded-full
                    border
                  "
                  style={{
                    borderColor:
                      colors.border,

                    color:
                      colors.gold,
                  }}
                >
                  <Check size={13} />
                </div>
              </div>
            </CardShell>
          ) : null}
        </div>


        {/* =================================================
            PRESENCE
        ================================================= */}

        <div
          className="
            mt-5
            flex
            items-center
            justify-center
            gap-2
            text-center
          "
        >
          <span
            className="
              h-px
              w-8
              bg-gradient-to-r
              from-transparent
              to-amber-400/55
            "
          />

          <span
            className="
              text-[8px]
              font-black
              uppercase
              tracking-[1.5px]
            "
            style={{
              color:
                colors.gold,
            }}
          >
            {morningCopy.presence}
          </span>

          <span
            className="
              h-px
              w-8
              bg-gradient-to-l
              from-transparent
              to-amber-400/55
            "
          />
        </div>


        {/* =================================================
            FINAL MESSAGE
        ================================================= */}

        <div
          className="
            mt-6
            text-center
          "
        >
          <p
            className="
              mx-auto
              max-w-[330px]
              text-[10px]
              font-semibold
              leading-5
            "
            style={{
              color:
                colors.textSoft,
            }}
          >
            {morningCopy.finalMessage}
          </p>
        </div>


        {/* =================================================
            ACTIONS
        ================================================= */}

        <div
          className="
            mt-6
            space-y-2
          "
        >
          <button
            type="button"
            onClick={() =>
              begin()
            }
            disabled={closing}
            data-testid="Begin-day-btn"
            className="
              group
              relative
              flex
              min-h-[56px]
              w-full
              items-center
              justify-center
              gap-2.5
              rounded-[18px]
              border
              px-5
              text-[13px]
              font-black
              active:scale-[0.99]
              disabled:pointer-events-none
              disabled:opacity-70
            "
            style={{
              background:
                "linear-gradient(135deg,#F4B72C 0%,#D99413 52%,#B97008 100%)",

              borderColor:
                "rgba(255,227,151,0.30)",

              color:
                colors.buttonText,

              boxShadow:
                isDark
                  ? "0 12px 30px rgba(209,139,18,.17)"
                  : "0 12px 28px rgba(183,116,8,.20)",
            }}
          >
            <Sunrise size={17} />

            <span>
              {t(
                "Begin My Day"
              )}
            </span>

            <ArrowRight
              size={16}
            />
          </button>


          <button
            type="button"
            onClick={() =>
              begin()
            }
            disabled={closing}
            data-testid="skip-morning-btn"
            className="
              w-full
              py-2.5
              text-center
              text-[11px]
              font-semibold
              disabled:pointer-events-none
              disabled:opacity-60
            "
            style={{
              color:
                colors.skip,
            }}
          >
            {t(
              "Skip For Now"
            )}
          </button>


          {!storage.get(
            StorageKeys.OnboardingComplete,
            false
          ) &&
          !(
            storage.get(
              StorageKeys.UserProfile,
              {}
            ) || {}
          ).fullName ? (
            <button
              type="button"
              disabled={closing}
              onClick={() => {
                begin(() => {
                  navigate(
                    "/onboarding"
                  );
                });
              }}
              data-testid="splash-join"
              className="
                mx-auto
                flex
                items-center
                gap-1
                py-1
                text-[10px]
                font-bold
                underline
                underline-offset-4
                disabled:pointer-events-none
                disabled:opacity-60
              "
              style={{
                color:
                  colors.gold,
              }}
            >
              {t(
                "regTitle"
              )}

              <ArrowRight
                size={11}
              />
            </button>
          ) : null}
        </div>


        {/* =================================================
            BOTTOM BRAND
        ================================================= */}

        <div
          className="
            mt-7
            text-center
          "
        >
          <p
            className="
              text-[8px]
              font-black
              uppercase
              tracking-[1.8px]
            "
            style={{
              color:
                colors.muted,
            }}
          >
            GurSetu · Made with ❤️ by CodeSphere Technologies
          </p>
        </div>
      </div>
    </div>
  );
}
